import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";

/** Which screen edge the panel is attached to. Affects drag direction. */
export type ResizeAnchor = "left" | "right";

export type UseResizableOptions = {
  initial: number;
  min: number;
  max: number;
  storageKey?: string;
  anchor?: ResizeAnchor;
};

export type UseResizableResult = {
  width: number;
  startResize: (e: MouseEvent) => void;
};

function readStored(
  storageKey: string | undefined,
  min: number,
  max: number,
): number | null {
  if (!storageKey) {
    return null;
  }
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const v = parseInt(raw, 10);
    if (Number.isNaN(v)) {
      return null;
    }
    if (v < min || v > max) {
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

/**
 * Track a width that the user can resize via a mouse-drag handle.
 * Returns the current width and an `onMouseDown` to put on the drag handle.
 * Width is clamped to [min, max] and optionally persisted to localStorage.
 */
export function useResizable({
  initial,
  min,
  max,
  storageKey,
  anchor = "left",
}: UseResizableOptions): UseResizableResult {
  const [width, setWidth] = useState<number>(
    () =>
      readStored(storageKey, min, max) ?? Math.max(min, Math.min(max, initial)),
  );
  // Holds the teardown for an in-flight drag, so we can clean up on unmount.
  const cleanupResizeRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (!storageKey) {
      return;
    }

    const timeout = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, String(width));
      } catch {
        // ignore quota / privacy errors
      }
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  }, [storageKey, width]);

  useEffect(() => {
    return () => {
      cleanupResizeRef.current?.();
    };
  }, []);

  const startResize = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      cleanupResizeRef.current?.();
      const startX = e.clientX;
      const startWidth = width;
      const prevCursor = document.body.style.cursor;
      const prevUserSelect = document.body.style.userSelect;

      const onMove = (ev: globalThis.MouseEvent) => {
        // A right-anchored panel grows as the mouse moves left.
        const delta =
          anchor === "right" ? startX - ev.clientX : ev.clientX - startX;
        const next = Math.min(max, Math.max(min, startWidth + delta));
        setWidth(next);
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
        cleanupResizeRef.current = null;
      };

      cleanupResizeRef.current = onUp;
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width, min, max, anchor],
  );

  return { width, startResize };
}
